import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

export interface MonetizationSummaryMetrics {
  totalPremiumUsers: number;
  premiumUsersLast30Days: number;
  upgradeClicksLast7Days: number;
  upgradeClicksLast30Days: number;
}

interface DailyMonetizationPoint {
  date: string;
  upgradeClicks: number;
  newPremiumUsers: number;
  totalPremiumUsersToDate: number;
}

interface PremiumSummaryCardProps {
  summary: MonetizationSummaryMetrics | null;
  className?: string;
  days?: 7 | 30 | 90;
  includeBots?: boolean;
}

const formatDateLabel = (raw: string): string => {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const PremiumSummaryCard: React.FC<PremiumSummaryCardProps> = ({
  summary,
  className,
  days = 30,
  includeBots = false
}) => {
  const [data, setData] = useState<DailyMonetizationPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axios.get<{
          success: boolean;
          data: DailyMonetizationPoint[];
        }>('/api/admin/metrics/monetization/trends', {
          params: { days, includeBots }
        });

        if (!res.data.success) {
          throw new Error('Failed to load monetization trends');
        }

        if (!cancelled) {
          setData(res.data.data);
        }
      } catch (err: any) {
        console.error('[PremiumSummaryCard] Failed to load trends', err);
        if (!cancelled) {
          setError('Failed to load premium trends.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [days, includeBots]);

  return (
    <div className={className}>
      <div className="admin-dashboard-card__label">Premium overview</div>
      <div className="admin-dashboard-card__value">
        {summary ? summary.totalPremiumUsers : '—'}
      </div>
      <div className="admin-dashboard-card__meta">
        {summary ? (
          <>
            {summary.upgradeClicksLast7Days} upgrade clicks in last 7 days ·{' '}
            {summary.premiumUsersLast30Days} new premium users in last 30 days
          </>
        ) : (
          'Premium metrics not available.'
        )}
      </div>

      <div className="admin-dashboard-premium-chart">
        {loading && data.length === 0 && (
          <div className="admin-dashboard-card__meta">Loading premium trend…</div>
        )}
        {error && data.length === 0 && (
          <div className="admin-dashboard-card__meta">{error}</div>
        )}
        {data.length > 0 && (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={data}
              margin={{ top: 10, right: 10, left: -20, bottom: 10 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--surface-subtle, #e5e7eb)"
              />
              <XAxis
                dataKey="date"
                tickFormatter={formatDateLabel}
                stroke="var(--text-muted, #6b7280)"
                tick={{ fill: 'var(--text-muted, #6b7280)', fontSize: 10 }}
              />
              <YAxis
                stroke="var(--text-muted, #6b7280)"
                tick={{ fill: 'var(--text-muted, #6b7280)', fontSize: 10 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--surface-elevated, #ffffff)',
                  border: '1px solid var(--surface-subtle, #e5e7eb)',
                  borderRadius: 4,
                  fontSize: 12
                }}
                labelFormatter={(label: string) => formatDateLabel(label)}
              />
              <Legend
                wrapperStyle={{ paddingTop: 4 }}
                formatter={(value: string) =>
                  value === 'upgradeClicks'
                    ? 'Upgrade clicks'
                    : value === 'newPremiumUsers'
                      ? 'New premium users'
                      : value
                }
              />
              <Bar
                dataKey="upgradeClicks"
                fill="#6366f1"
                name="upgradeClicks"
              />
              <Bar
                dataKey="newPremiumUsers"
                fill="#10b981"
                name="newPremiumUsers"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
        {!loading && !error && data.length === 0 && (
          <div className="admin-dashboard-card__meta">
            No premium upgrade activity recorded in the selected window yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumSummaryCard;



